import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
} from '@mui/material';
import QuizIcon from '@mui/icons-material/Quiz';
import TimerIcon from '@mui/icons-material/Timer';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';

import TopNav from './Navbar';
import LeftNavbar from './LeftNav';
import Breadcrumb from './BreadCrump';

const API_BASE = 'http://196.216.9.34:5020/api/exams';

const getStatusChip = (status, score) => {
  switch (status) {
    case 'Upcoming':
      return <Chip label="Upcoming" color="info" size="small" icon={<TimerIcon />} />;
    case 'Ongoing':
      return <Chip label="Ongoing" color="warning" size="small" icon={<PlayArrowIcon />} />;
    case 'Completed':
      return (
        <Tooltip title="View your score">
          <Chip
            label={`Score: ${score || 'N/A'}%`}
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
            icon={<AssignmentTurnedInIcon />}
          />
        </Tooltip>
      );
    case 'Failed':
      return <Chip label="Failed" color="error" size="small" icon={<CancelScheduleSendIcon />} />;
    default:
      return <Chip label="Unknown" size="small" />;
  }
};

const Exams = () => {
  const [examData, setExamData] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [previousExams, setPreviousExams] = useState([]);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const fetchExams = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/student-exams`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error(`Failed to fetch exams: ${response.status}`);
      const data = await response.json();
      setExamData(data);
    } catch (error) {
      console.error('Error fetching exams:', error.message);
    }
  };

  const fetchPreviousExams = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/student-results`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Failed to fetch previous exams`);
      const data = await response.json();

      const formatted = data.map((exam) => ({
        id: exam.resultId,
        title: exam.examTitle,
        date: new Date(exam.attempted_at).toLocaleDateString(),
        score: exam.percentage.toFixed(2),
        status: exam.passed ? 'Completed' : 'Failed',
      }));

      setPreviousExams(formatted);
    } catch (error) {
      console.error('Error fetching previous exams:', error.message);
    }
  };

  useEffect(() => {
    fetchExams();
    fetchPreviousExams();
  }, [token]);

  return (
    <Box sx={{ display: 'flex' }}>
      <LeftNavbar />
      <Box sx={{ flexGrow: 1, ml: { lg: '240px' } }}>
        <TopNav />
        <Box sx={{ p: 10, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
          <Breadcrumb currentPage="Exams" />

          <Typography variant="h4" fontWeight="bold" sx={{ mb: 4, color: '#ff6600' }}>
            <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Exams
          </Typography>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(e, newValue) => setTabValue(newValue)}
            sx={{ mb: 3 }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="Ongoing Exams" />
            <Tab label="Previously Attempted" />
          </Tabs>

          {/* Ongoing Exams */}
          {tabValue === 0 && (
            <Grid container spacing={3}>
              {examData.map((exam) => (
                <Grid item xs={12} md={6} key={exam.id}>
                  <Card sx={{ boxShadow: 3, borderLeft: '6px solid #ff6600' }}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                        <Avatar sx={{ bgcolor: '#ff6600' }}>
                          <QuizIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6">{exam.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {exam.subject} | {exam.type} | {exam.duration}
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Scheduled At:</strong>{' '}
                        {new Date(exam.scheduledAt).toLocaleString()}
                      </Typography>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {getStatusChip(exam.status, exam.score)}
                        {exam.status === 'Ongoing' && exam.score == null && (
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PlayArrowIcon />}
                            onClick={() => navigate('/automatic-exam-two', { state: { exam } })}
                            sx={{
                              bgcolor: '#ff6600',
                              '&:hover': { bgcolor: '#e65c00' },
                            }}
                          >
                            Start Exam
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Previously Attempted Exams */}
          {tabValue === 1 && (
            <Paper elevation={3}>
              <Table>
                <TableHead sx={{ backgroundColor: '#ff6600' }}>
                  <TableRow>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Title
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <TimerIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Date
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <AssignmentTurnedInIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Score
                    </TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                        <PlayArrowIcon sx={{ mr: 1, verticalAlign: 'middle' }} /> Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previousExams.map((exam) => (
                    <TableRow key={exam.id}>
                      <TableCell>{exam.title}</TableCell>
                      <TableCell>{exam.date}</TableCell>
                      <TableCell>{exam.score}%</TableCell>
                      <TableCell>{getStatusChip(exam.status, exam.score)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Exams;
